import api from '@/services/utils/apiInstance.ts'
import type { ApiError, CustomAxiosRequestConfig } from '@/types/services/utils/axios.type.ts';
import type { AxiosError } from 'axios';
import { displayResponseError } from '@/utils/toastUtils.ts';
import { useAuthStore } from '@/stores/authStore.ts';

const EXCLUDED_ROUTE = ['/auth/login', '/auth/refresh-token', '/auth/logout', '/user/reset-password']

function getCookie(name: string) {
  const match = document.cookie.match(new RegExp('(^| )' + name + '=([^;]+)'))
  return match ? match[2] : null
}

const setup = () => {
  const authStore = useAuthStore()

  api.interceptors.request.use(config => {
    // Back-office CSRF cookie — distinct from the client portal's csrf_token.
    const csrf = getCookie('bo_csrf_token')
    if (csrf) {
      config.headers['X-CSRF-Token'] = csrf
    }
    return config
  })

  api.interceptors.response.use(res => res, async error => {
    const { response } = error
    const config = error.config as CustomAxiosRequestConfig;
    const errorKey = (response.data as ApiError)?.key;

    if (!EXCLUDED_ROUTE.some(route => config.url?.includes(route))) {
      if (response?.status === 401 && !config._retry) {
        if ((errorKey === 'expired_token_error_key' || errorKey === 'invalid_token_error_key')) {
          config._retry = true
          try {
            await api.get('/auth/refresh-token')
            return api(config)
          } catch {
            await authStore.logout()
            displayResponseError(error as AxiosError<ApiError>)
            return Promise.reject(error)
          }
        }
      }

      if (response?.status === 401) {
        await authStore.logout()
        displayResponseError(error as AxiosError<ApiError>)
        return Promise.reject(error)
      }

      if (response.status === 403 && errorKey === 'user_not_allowed_error_key') {
        await authStore.logout()
        displayResponseError(error as AxiosError<ApiError>)
        return Promise.reject(error)
      }
    }

    if (!config.disableGlobalError) {
      displayResponseError(error as AxiosError<ApiError>);
    }

    return Promise.reject(error)
  })
}

export default setup
